import React, { useMemo, useRef, useState } from 'react'

// Leaflet TS types:
import Leaflet from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMapEvents, Tooltip, Circle } from 'react-leaflet'
import GeosearchUtils, { ErrorResponse, RequestLangugage, UnifiedErrorResponse } from '../utils/GeosearchUtils'
import { useTranslation } from 'react-i18next'

interface MapDetailPlaceSelectionProps {
    newPlacePosition: Leaflet.LatLng
    setAddressString: (address: string) => void
    setNewPlace: (newPlace: Leaflet.LatLng) => void
}

const MapDetailPlaceSelection: React.FC<MapDetailPlaceSelectionProps> = ({ newPlacePosition, setAddressString, setNewPlace }) => {

    const [newPlacePositionUpdated, setNewPlacePositionUpdated] = useState(newPlacePosition)
    const [addressStringInitiated, setAddressStringInitiated] = useState(false)

    const { t } = useTranslation();

    const dragableMarkerRef = useRef<Leaflet.Marker>(null);
    const geosearchUtils = new GeosearchUtils();

    const processGeosearchError = (error: UnifiedErrorResponse): void => {
        console.error("In MapDetailPlaceSelection processGeosearchError: ", error);
        switch (error.type) {
            case 'GEOCODING_ERROR': {
                if (error.code === GeosearchUtils.UNABLE_TO_GEOCODE_ERROR_MESSAGE) {
                    setAddressString(t("place.creation.unable-to-geocode"))
                } else {
                    setAddressString(t("place.creation.n-a"))
                }
                break
            }
            case 'NO_ADDRESS_FOUND':
            case 'NETWORK_ERROR':
            case 'UNKNOWN_ERROR':
            default: {
                setAddressString(t("place.creation.n-a"))
            }
        }
    }

    // Initial place & address string set:
    if (!addressStringInitiated) {
        setNewPlace(newPlacePosition)
        geosearchUtils.getAddress(
            newPlacePosition,
            // TODO why hardcoded english?
            RequestLangugage.ENGLISH,
            (searchResult, oneLineAddress) => setAddressString(oneLineAddress),
            (error) => processGeosearchError(error)
        )
        setAddressStringInitiated(true)
    }


    const DragablePlacePositionUpdateMarker = () => {


        // Update position on drag
        const newPostionEventHandler = useMemo(
            () => ({
                dragend() {
                    const marker = dragableMarkerRef.current
                    if (marker != null) {
                        console.log("Updated latlng DRAG: ", marker.getLatLng().toString())

                        // Address string update:
                        setNewPlace(marker.getLatLng())
                        geosearchUtils.getAddress(
                            marker.getLatLng(),
                            RequestLangugage.ENGLISH,
                            (searchResult, oneLineAddress) => setAddressString(oneLineAddress),
                            (error) => processGeosearchError(error)
                        )

                        setNewPlacePositionUpdated(marker.getLatLng())
                    }
                }
            }),
            []
        )

        // Update position on click
        useMapEvents({
            click(event) {

                // Address string update:
                setNewPlace(event.latlng)
                geosearchUtils.getAddress(
                    event.latlng,
                    RequestLangugage.ENGLISH,
                    (searchResult, oneLineAddress) => setAddressString(oneLineAddress),
                    (error) => processGeosearchError(error)
                )

                setNewPlacePositionUpdated(event.latlng)
            }
        })

        return (
            <Marker
                position={newPlacePositionUpdated}
                ref={dragableMarkerRef}
                eventHandlers={newPostionEventHandler}
                draggable
                autoPan
            >

                {/* < TODO what to put in this popup?  Popup> */}
                < Popup>
                    Do this do that
                </Popup>
            </Marker >
        )
    }


    return (
        <>
            <MapContainer
                center={[newPlacePosition.lat, newPlacePosition.lng]}
                zoom={30}
                scrollWheelZoom={false}
                attributionControl={false}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* TODO is needed this enhancment? What about gradient from inside out? */}
                <Circle center={newPlacePositionUpdated} radius={5} />
                <DragablePlacePositionUpdateMarker />
            </MapContainer>
        </>
    )
}

export default MapDetailPlaceSelection;